<template>
  <div class="invoice-page-center">
    <!-- Summary Cards -->
    <div class="earnings-summary" v-if="earningSummary">
      <div class="summary-card pending">
        <div class="summary-icon"><i class="fas fa-clock"></i></div>
        <div class="summary-content">
          <span class="summary-label">Komisi Belum Dicairkan</span>
          <span class="summary-value">{{ formatCurrency(earningSummary.total_pending) }}</span>
        </div>
      </div>
      <div class="summary-card paid">
        <div class="summary-icon"><i class="fas fa-check-circle"></i></div>
        <div class="summary-content">
          <span class="summary-label">Komisi Sudah Dibayar</span>
          <span class="summary-value">{{ formatCurrency(earningSummary.total_paid) }}</span>
        </div>
      </div>
      <div class="summary-card reading">
        <div class="summary-icon"><i class="fas fa-book-reader"></i></div>
        <div class="summary-content">
          <span class="summary-label">Total Waktu Baca (Buku Anda)</span>
          <span class="summary-value">{{ formatDuration(earningSummary.total_read_seconds) }}</span>
        </div>
      </div>
    </div>

    <!-- Author Earnings Table -->
    <div class="accounts-table-container" v-if="authorEarnings.length > 0">
      <h3 style="margin: 0 0 12px 0; color: #2d3748;">Riwayat Komisi Penulis</h3>
      <div style="overflow-x: auto;">
        <table class="accounts-table">
          <thead>
            <tr>
              <th>Periode</th>
              <th>Waktu Baca</th>
              <th>Total Pool</th>
              <th>Komisi Anda</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in authorEarnings" :key="item.id">
              <td>{{ formatPeriod(item.period_start, item.period_end) }}</td>
              <td>{{ formatDuration(item.author_read_seconds) }} / {{ formatDuration(item.total_read_seconds) }}</td>
              <td>{{ formatCurrency(item.revenue_pool) }}</td>
              <td><strong>{{ formatCurrency(item.amount) }}</strong></td>
              <td>
                <span :class="'status-badge status-' + item.status">
                  {{ item.status === 'calculated' ? 'Belum Dibayar' : 'Sudah Dibayar' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Existing Transaction Table -->
    <div class="accounts-table-container">
      <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 18px; flex-wrap: wrap; gap: 12px;">
        <!-- Filters -->
        <div style="display: flex; gap: 12px; flex-wrap: wrap;">
          <!-- Transaction Type Filter -->
          <div>
            <label style="font-weight: 500; margin-bottom: 4px; display: block;">Jenis Transaksi</label>
            <select v-model="filters.transaction_type" @change="onFilterChange" style="padding: 6px 10px; border-radius: 4px; border: 1px solid #ced4da;">
              <option value="">Semua</option>
              <option v-for="type in transactionTypes" :key="type.value" :value="type.value">
                {{ type.display }}
              </option>
            </select>
          </div>
          <!-- Date Range Filter -->
          <div>
            <label style="font-weight: 500; margin-bottom: 4px; display: block;">Dari</label>
            <input type="date" v-model="filters.date_from" @change="onFilterChange" style="padding: 6px 10px; border-radius: 4px; border: 1px solid #ced4da;" />
          </div>
          <div>
            <label style="font-weight: 500; margin-bottom: 4px; display: block;">Sampai</label>
            <input type="date" v-model="filters.date_to" @change="onFilterChange" style="padding: 6px 10px; border-radius: 4px; border: 1px solid #ced4da;" />
          </div>
        </div>
        <!-- Sort Dropdown -->
        <div style="position: relative;">
          <label style="font-weight: 500; margin-bottom: 4px; display: block;">Urutkan</label>
          <button @click="showSortDropdown = !showSortDropdown"
            style="background: #f7fafc; border: 1px solid #ced4da; border-radius: 4px; padding: 6px 16px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 8px;">
            <span>
              <i :class="sortOrder === 'desc' ? 'fas fa-sort-amount-down-alt' : 'fas fa-sort-amount-up'" style="margin-right: 6px;"></i>
              {{ sortOrder === 'desc' ? 'Tanggal Terbaru' : 'Tanggal Terlama' }}
            </span>
            <i class="fas fa-caret-down"></i>
          </button>
          <div v-if="showSortDropdown" class="sort-dropdown" @mouseleave="showSortDropdown = false">
            <div @click="setSortOrder('desc')" :class="{active: sortOrder==='desc'}">
              <i class="fas fa-sort-amount-down-alt"></i> Tanggal Terbaru
            </div>
            <div @click="setSortOrder('asc')" :class="{active: sortOrder==='asc'}">
              <i class="fas fa-sort-amount-up"></i> Tanggal Terlama
            </div>
          </div>
        </div>
      </div>
      <!-- Table -->
      <div style="overflow-x: auto;">
        <table class="accounts-table">
          <thead>
            <tr>
              <th>Tanggal</th>
              <th>Jumlah</th>
              <th>Jenis</th>
              <th>Deskripsi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in transactions" :key="item.id">
              <td>{{ formatDate(item.created_at) }}</td>
              <td>{{ formatCurrency(item.amount) }}</td>
              <td>{{ item.transaction_type_display || item.transaction_type }}</td>
              <td>{{ item.description }}</td>
            </tr>
            <tr v-if="transactions.length === 0">
              <td colspan="4" class="no-accounts">Tidak ada transaksi.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Pagination Controls -->
      <div style="display: flex; justify-content: flex-end; align-items: center; margin-top: 18px; gap: 8px;">
        <button
          :disabled="page === 1"
          @click="changePage(page - 1)"
          style="padding: 6px 12px; border-radius: 4px; border: 1px solid #ced4da; background: #f7fafc; cursor: pointer;"
        >
          &laquo; Prev
        </button>
        <span>Halaman {{ page }} dari {{ totalPages }}</span>
        <button
          :disabled="page === totalPages"
          @click="changePage(page + 1)"
          style="padding: 6px 12px; border-radius: 4px; border: 1px solid #ced4da; background: #f7fafc; cursor: pointer;"
        >
          Next &raquo;
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.invoice-page-center {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 60vh;
  background: #f7fafc;
  padding: 2.5rem 0 2rem 0;
}

.accounts-table-container {
  margin: 2.5rem auto 0 auto;
  width: 100%;
  max-width: 1100px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.07);
  padding: 2rem 2.5rem;
}

.accounts-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  margin-top: 1.5rem;
  font-size: 1rem;
}

.accounts-table thead th {
  background: #f7fafc;
  color: #2d3748;
  font-weight: 600;
  padding: 1rem 0.75rem;
  border-bottom: 2px solid #e2e8f0;
  text-align: left;
}

.accounts-table tbody td {
  padding: 0.9rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
  color: #374151;
  vertical-align: middle;
}

.accounts-table tbody tr:last-child td {
  border-bottom: none;
}

.accounts-table tbody tr:hover {
  background: #f1f5f9;
  transition: background 0.2s;
}

.no-accounts {
  text-align: center;
  color: #a0aec0;
  font-style: italic;
  padding: 1.5rem 0;
}

.sort-dropdown {
  position: absolute;
  right: 0;
  top: 38px;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  min-width: 170px;
  z-index: 10;
}
.sort-dropdown > div {
  padding: 10px 18px;
  cursor: pointer;
  font-weight: 500;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sort-dropdown > div.active, .sort-dropdown > div:hover {
  background: #f7fafc;
}

.earnings-summary {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
}

.summary-card {
  flex: 1;
  min-width: 200px;
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.07);
}

.summary-card .summary-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.summary-card.pending .summary-icon { background: #fef3c7; color: #d97706; }
.summary-card.paid .summary-icon { background: #d1fae5; color: #059669; }
.summary-card.reading .summary-icon { background: #dbeafe; color: #2563eb; }

.summary-content {
  display: flex;
  flex-direction: column;
}

.summary-label {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge.status-calculated {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.status-paid {
  background: #d1fae5;
  color: #065f46;
}

</style>

<script>
export default {
  name: 'MyEarnings',
  data() {
    return {
      transactions: [],
      transactionTypes: [],
      authorEarnings: [],
      earningSummary: null,
      filters: {
        transaction_type: '',
        date_from: '',
        date_to: ''
      },
      page: 1,
      pageSize: 10,
      total: 0,
      sortOrder: 'desc',
      showSortDropdown: false,
      loading: false
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    }
  },
  methods: {
    formatPeriod(start, end) {
      if (!start || !end) return '-';
      const s = new Date(start);
      const e = new Date(end);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Ags', 'Sep', 'Okt', 'Nov', 'Des'];
      return `${s.getDate()} ${months[s.getMonth()]} ${s.getFullYear()} - ${e.getDate()} ${months[e.getMonth()]} ${e.getFullYear()}`;
    },
    formatDuration(seconds) {
      if (!seconds || seconds === 0) return '0 menit';
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      if (h > 0) return `${h}j ${m}m`;
      return `${m} menit`;
    },
    async fetchAuthorEarnings() {
      try {
        const token = localStorage.getItem('token');
        const [summaryRes, listRes] = await Promise.all([
          fetch(`${process.env.VUE_APP_API_BASE_URL}/api/author-earnings/summary/`, {
            headers: { 'Authorization': token ? `Bearer ${token}` : '' }
          }),
          fetch(`${process.env.VUE_APP_API_BASE_URL}/api/author-earnings/`, {
            headers: { 'Authorization': token ? `Bearer ${token}` : '' }
          })
        ]);
        if (summaryRes.ok) {
          this.earningSummary = await summaryRes.json();
        }
        if (listRes.ok) {
          const data = await listRes.json();
          this.authorEarnings = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error('Failed to fetch author earnings:', e);
      }
    },
    async fetchTransactionTypes() {
      // Fetch choices for transaction_type filter
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/transactions/choices/`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });
        if (!response.ok) throw new Error('Gagal memuat jenis transaksi');
        const data = await response.json();
        // Expecting: [{value: 'royalty', display: 'Royalti'}, ...]
        this.transactionTypes = Array.isArray(data) ? data : [];
      } catch (e) {
        this.transactionTypes = [];
      }
    },
    async fetchTransactions() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        params.append('page', this.page);
        params.append('page_size', this.pageSize);
        params.append('ordering', this.sortOrder === 'desc' ? '-created_at' : 'created_at');
        if (this.filters.transaction_type) params.append('transaction_type', this.filters.transaction_type);
        if (this.filters.date_from) params.append('created_at__gte', this.filters.date_from);
        if (this.filters.date_to) params.append('created_at__lte', this.filters.date_to);
        const url = `${process.env.VUE_APP_API_BASE_URL}/api/transactions/?${params.toString()}`;
        const response = await fetch(url, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });
        if (!response.ok) throw new Error('Gagal memuat transaksi');
        const data = await response.json();
        this.transactions = Array.isArray(data.results) ? data.results : [];
        this.total = data.count || this.transactions.length;
      } catch (e) {
        this.transactions = [];
        this.total = 0;
      }
      this.loading = false;
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      if (isNaN(d)) return '-';
      return d.toLocaleDateString('id-ID');
    },
    formatCurrency(amount) {
      if (typeof amount !== 'number') amount = Number(amount);
      if (isNaN(amount)) return '-';
      return amount.toLocaleString('id-ID', { style: 'currency', currency: 'IDR' });
    },
    setSortOrder(order) {
      this.sortOrder = order;
      this.showSortDropdown = false;
      this.page = 1;
      this.fetchTransactions();
    },
    onFilterChange() {
      this.page = 1;
      this.fetchTransactions();
    },
    changePage(newPage) {
      if (newPage < 1 || newPage > this.totalPages) return;
      this.page = newPage;
      this.fetchTransactions();
    }
  },
  mounted() {
    this.fetchTransactionTypes();
    this.fetchTransactions();
    this.fetchAuthorEarnings();
  }
}
</script>